import { useEffect } from "react";
import { Button } from "@mui/material";
import { closeSnackbar, enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getCurrentUserId } from "../../Helpers/authHelpers";

type PushPayload = {
  title?: string;
  body?: string;
  url?: string;
};

// The service worker forwards pushes here when the recipient's own tab is
// focused, instead of raising an OS notification. It also asks this listener
// who is signed in, since it has no session of its own.
const PushMessageListener = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("profile");

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "WHO_IS_ACTIVE") {
        event.ports[0]?.postMessage({ userId: getCurrentUserId() });
        return;
      }

      if (event.data?.type !== "PUSH_RECEIVED") return;

      const payload: PushPayload = event.data.payload || {};
      const key = `push-${Date.now()}`;

      enqueueSnackbar([payload.title, payload.body].filter(Boolean).join(" — "), {
        key,
        variant: "info",
        autoHideDuration: 8000,
        action: payload.url ? (
          <Button
            size="small"
            sx={{ color: "inherit" }}
            onClick={() => {
              closeSnackbar(key);
              navigate(payload.url as string);
            }}
          >
            {t("profile.pushOpen")}
          </Button>
        ) : undefined,
      });
    };

    navigator.serviceWorker.addEventListener("message", handleMessage);
    return () =>
      navigator.serviceWorker.removeEventListener("message", handleMessage);
  }, [navigate, t]);

  return null;
};

export default PushMessageListener;
